import { Link } from 'react-router-dom';
import { IconAlertTriangle, IconInfo, IconLightbulb } from '../atoms';
import type { HelpBlock } from './types';

/**
 * ArticleBody — renders help content blocks.
 *
 * Deliberately the ONLY place help prose becomes DOM. HelpTip renders a preview
 * through the same component as the full article page, so a callout cannot look
 * like one thing in a tooltip and another on the page.
 *
 * Prose here is set wider-line-height and larger than the app's dense operator
 * UI on purpose: this is the one surface in the product somebody READS rather
 * than scans.
 */

const CALLOUT_TONE = {
  tip: {
    bg: 'var(--brand-primary-bg)',
    border: 'var(--brand-accent)',
    fg: 'var(--brand-primary)',
    Icon: IconLightbulb,
    label: 'Tip',
  },
  note: {
    bg: 'var(--color-status-gray-bg)',
    border: 'var(--color-border-strong)',
    fg: 'var(--color-ink-2)',
    Icon: IconInfo,
    label: 'Note',
  },
  warn: {
    bg: 'var(--color-status-amber-bg)',
    border: 'var(--color-status-amber)',
    fg: 'var(--color-status-amber)',
    Icon: IconAlertTriangle,
    label: 'Careful',
  },
} as const;

const proseStyle: React.CSSProperties = {
  margin: '0 0 var(--sp-4)',
  fontSize: 'var(--fs-lg)',
  lineHeight: 'var(--lh-relaxed)',
  color: 'var(--color-ink-2)',
};

function Block({ block }: { block: HelpBlock }) {
  switch (block.kind) {
    case 'text':
      return <p style={proseStyle}>{block.text}</p>;

    case 'heading':
      return (
        <h2 style={{
          margin: 'var(--sp-6) 0 var(--sp-3)',
          fontSize: 'var(--fs-xl)',
          fontWeight: 'var(--fw-semibold)',
          color: 'var(--color-ink)',
          letterSpacing: 'var(--ls-tight)',
        }}>{block.text}</h2>
      );

    case 'steps':
      return (
        <div style={{ marginBottom: 'var(--sp-4)' }}>
          {block.intro && <p style={proseStyle}>{block.intro}</p>}
          <ol style={{ margin: 0, paddingLeft: 0, listStyle: 'none', counterReset: 'help-step' }}>
            {block.steps.map((s, i) => (
              <li
                key={i}
                style={{
                  display: 'flex',
                  gap: 'var(--sp-3)',
                  alignItems: 'flex-start',
                  marginBottom: 'var(--sp-3)',
                  fontSize: 'var(--fs-lg)',
                  lineHeight: 'var(--lh-relaxed)',
                  color: 'var(--color-ink-2)',
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    flex: '0 0 auto',
                    width: 24,
                    height: 24,
                    marginTop: 2,
                    borderRadius: '50%',
                    background: 'var(--brand-primary)',
                    color: 'var(--brand-on-primary)',
                    fontSize: 'var(--fs-sm)',
                    fontWeight: 'var(--fw-semibold)',
                    display: 'grid',
                    placeItems: 'center',
                  }}
                >{i + 1}</span>
                <span>{s}</span>
              </li>
            ))}
          </ol>
        </div>
      );

    case 'bullets':
      return (
        <div style={{ marginBottom: 'var(--sp-4)' }}>
          {block.intro && <p style={proseStyle}>{block.intro}</p>}
          <ul style={{ margin: 0, paddingLeft: 'var(--sp-5)' }}>
            {block.items.map((s, i) => (
              <li key={i} style={{
                marginBottom: 'var(--sp-2)',
                fontSize: 'var(--fs-lg)',
                lineHeight: 'var(--lh-relaxed)',
                color: 'var(--color-ink-2)',
              }}>{s}</li>
            ))}
          </ul>
        </div>
      );

    case 'defs':
      return (
        <div style={{ marginBottom: 'var(--sp-4)' }}>
          {block.intro && <p style={proseStyle}>{block.intro}</p>}
          <dl style={{ margin: 0 }}>
            {block.items.map((d, i) => (
              <div
                key={i}
                style={{
                  padding: 'var(--sp-3) var(--sp-4)',
                  marginBottom: 'var(--sp-2)',
                  background: 'var(--color-card)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--r-md)',
                }}
              >
                <dt style={{
                  fontWeight: 'var(--fw-semibold)',
                  color: 'var(--color-ink)',
                  fontSize: 'var(--fs-md)',
                  marginBottom: 2,
                }}>{d.term}</dt>
                <dd style={{
                  margin: 0,
                  fontSize: 'var(--fs-md)',
                  lineHeight: 'var(--lh-relaxed)',
                  color: 'var(--color-ink-2)',
                }}>{d.meaning}</dd>
              </div>
            ))}
          </dl>
        </div>
      );

    case 'callout': {
      const tone = CALLOUT_TONE[block.tone];
      const { Icon } = tone;
      return (
        <aside
          style={{
            display: 'flex',
            gap: 'var(--sp-3)',
            padding: 'var(--sp-4)',
            margin: '0 0 var(--sp-4)',
            background: tone.bg,
            borderLeft: `3px solid ${tone.border}`,
            borderRadius: 'var(--r-md)',
          }}
        >
          <span style={{ flex: '0 0 auto', color: tone.fg, marginTop: 1 }}>
            <Icon size={18} aria-label={tone.label} />
          </span>
          <div>
            {block.title && (
              <div style={{
                fontWeight: 'var(--fw-semibold)',
                color: 'var(--color-ink)',
                fontSize: 'var(--fs-md)',
                marginBottom: 2,
              }}>{block.title}</div>
            )}
            <div style={{
              fontSize: 'var(--fs-md)',
              lineHeight: 'var(--lh-relaxed)',
              color: 'var(--color-ink-2)',
            }}>{block.text}</div>
          </div>
        </aside>
      );
    }

    case 'shot':
      return (
        <figure style={{ margin: '0 0 var(--sp-5)' }}>
          <img
            src={block.src}
            alt={block.alt}
            loading="lazy"
            style={{
              display: 'block',
              width: '100%',
              height: 'auto',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--r-lg)',
              background: 'var(--color-card)',
            }}
          />
          {block.caption && (
            <figcaption style={{
              marginTop: 'var(--sp-2)',
              fontSize: 'var(--fs-sm)',
              color: 'var(--color-ink-3)',
              lineHeight: 'var(--lh-normal)',
            }}>{block.caption}</figcaption>
          )}
        </figure>
      );
  }
}

export function ArticleBody({ blocks }: { blocks: HelpBlock[] }) {
  return (
    <div>
      {blocks.map((b, i) => <Block key={i} block={b} />)}
    </div>
  );
}

/** A compact link to another article — used for "related reading" rows. */
export function ArticleLink({ slug, title, summary }: { slug: string; title: string; summary?: string }) {
  return (
    <Link
      to={`/help/${slug}`}
      style={{
        display: 'block',
        padding: 'var(--sp-3) var(--sp-4)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--r-md)',
        background: 'var(--color-card)',
        textDecoration: 'none',
      }}
    >
      <div style={{
        fontWeight: 'var(--fw-medium)',
        color: 'var(--brand-primary)',
        fontSize: 'var(--fs-md)',
      }}>{title}</div>
      {summary && (
        <div style={{
          marginTop: 2,
          fontSize: 'var(--fs-sm)',
          color: 'var(--color-ink-3)',
          lineHeight: 'var(--lh-normal)',
        }}>{summary}</div>
      )}
    </Link>
  );
}
